import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';
import type { AskAllTrailsArgs, AskLocation } from '@/utils/askAllTrailsApi';

/**
 * Ask AllTrails mode on /explore. While it's active the sidebar shows Ask, the map shows only Ask's pins, and the hosting
 * page's own searches and pin updates are paused without touching Explore's state.
 *
 * Deliberately left out of `useResetState`, so Back from a page can return to Ask.
 */
export type AskState = {
  isActive: boolean;
  /**
   * Whether a page that hosts Ask is mounted. Ask stays active as the user moves between pages, but only a host page shows
   * it; a page becomes a host by calling `useAskMode`.
   */
  isAvailable: boolean;
  /**
   * The question being answered, as the request it sends. Built once, at submit, with the viewport copied in, and never
   * rebuilt from live state: panning the map shouldn't change the request.
   *
   * Kept when Ask closes with an answer, so reopening it shows that answer from the cache.
   */
  args: AskAllTrailsArgs | undefined;
};

export const initialState: AskState = {
  isActive: false,
  isAvailable: false,
  args: undefined
};

export const askSlice = createSlice({
  name: 'ask',
  initialState,
  reducers: {
    askQuestion: {
      reducer: (state, action: PayloadAction<AskAllTrailsArgs>) => {
        state.args = action.payload;
      },
      prepare: ({ location, question }: { location?: AskLocation; question: string }) => ({
        payload: { requestId: nanoid(), request: { query: question, ...(location && { location }) } }
      })
    },
    clearAskQuestion: state => {
      state.args = undefined;
    },
    enterAsk: state => {
      state.isActive = true;
    },
    exitAsk: state => {
      state.isActive = false;
    },
    setIsAskAvailable: (state, action: PayloadAction<boolean>) => {
      state.isAvailable = action.payload;
    }
  }
});

export const { askQuestion, clearAskQuestion, enterAsk, exitAsk, setIsAskAvailable } = askSlice.actions;

export default askSlice.reducer;
